<template>
  <div class="envios-lista">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-paper-plane"></i>
        Gestão de Envios
      </h1>
      <p class="page-subtitle">Gerencie os envios de documentos dos candidatos</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Buscar</label>
          <input 
            v-model="filters.search" 
            type="text" 
            placeholder="Nome, CPF ou E-mail do candidato"
            class="form-control"
            @input="debounceSearch"
          />
        </div>
        
        <div class="filter-group">
          <label>Concurso</label>
          <select v-model="filters.concursoId" class="form-control" @change="applyFilters">
            <option value="">Todos os concursos</option>
            <option v-for="concurso in concursos" :key="concurso.id" :value="concurso.id">
              {{ concurso.prefeitura }} - {{ concurso.titulo }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Status</label>
          <select v-model="filters.status" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="P">Pendente</option>
            <option value="A">Validado</option>
            <option value="R">Rejeitado</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Data Início</label>
            <input 
            v-model="filters.dataInicio" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-group">
          <label>Data Fim</label>
            <input 
            v-model="filters.dataFim" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="exportData" class="btn btn-success">
            <i class="fas fa-download"></i>
            Exportar
          </button>
        </div>
      </div>
    </div>

    <!-- Estatísticas -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-paper-plane"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.total_envios || 0 }}</div>
          <div class="stat-label">Total de Envios</div>
        </div>
      </div>
      <div class="stat-card pending">
        <div class="stat-icon">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.envios_pendentes || 0 }}</div>
          <div class="stat-label">Pendentes de Validação</div>
        </div>
      </div>
      <div class="stat-card success">
        <div class="stat-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.envios_validados || 0 }}</div>
          <div class="stat-label">Validados</div>
        </div>
      </div>
      <div class="stat-card danger">
        <div class="stat-icon">
          <i class="fas fa-times-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.envios_rejeitados || 0 }}</div>
          <div class="stat-label">Rejeitados</div>
        </div>
      </div>
    </div>

    <!-- Tabela de Envios (agrupado por inscrição) -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando grupos de envios...</p>
      </div>

      <div v-else-if="grupos.length === 0" class="empty-state">
      <i class="fas fa-paper-plane"></i>
        <h3>Nenhum grupo encontrado</h3>
        <p>Não há envios que correspondam aos filtros aplicados.</p>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>ID Inscrição</th>
                <th>Candidato</th>
                <th>CPF</th>
                <th>Concurso</th>
                <th>Cargo</th>
                <th>Envios</th>
                <th>Pendentes</th>
                <th class="actions-column">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="grupo in grupos" :key="grupo.inscricaoId ?? grupo.inscricao_id">
                <td><strong>#{{ (grupo.inscricaoId ?? grupo.inscricao_id) || grupo.inscricao?.id || 'N/A' }}</strong></td>
                <td>
                  <div class="candidate-info">
                    <strong>{{ grupo.inscricao?.cadastro?.nome }}</strong>
                    <small class="text-muted">{{ grupo.inscricao?.cadastro?.email }}</small>
                  </div>
                </td>
                <td>{{ formatCPF(grupo.inscricao?.cadastro?.cpf) }}</td>
                <td>
                  <div class="concurso-info">
                    <strong>{{ grupo.inscricao?.concurso?.prefeitura }}</strong>
                    <small class="text-muted">{{ grupo.inscricao?.concurso?.titulo }}</small>
                  </div>
                </td>
                <td>{{ grupo.inscricao?.vaga?.cargo }}</td>
                <td>
                  <span class="badge badge-info">
                    <i class="fas fa-layer-group"></i>
                    {{ (grupo.totalEnvios ?? grupo.total_envios) || 0 }}
                  </span>
                </td>
                <td>
                  <span class="badge badge-warning">{{ grupo.pendentes || 0 }}</span>
                </td>
                <td class="actions">
                  <button 
                    @click="viewDetails(grupo)" 
                    class="btn btn-sm btn-info"
                    title="Validar envios da inscrição"
                  >
                    <i class="fas fa-check-double"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="pagination-wrapper">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button @click="changePage(currentPage - 1)" class="page-link">Anterior</button>
              </li>
              <li 
                v-for="page in visiblePages" 
                :key="page" 
                class="page-item" 
                :class="{ active: page === currentPage }"
              >
                <button @click="changePage(page)" class="page-link">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button @click="changePage(currentPage + 1)" class="page-link">Próxima</button>
              </li>
            </ul>
          </nav>
          <div class="pagination-info">
            Mostrando {{ (currentPage - 1) * perPage + 1 }} a {{ Math.min(currentPage * perPage, totalItems) }} de {{ totalItems }} inscrições
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Validação por Inscrição -->
    <div v-if="showValidacaoModal && grupoSelecionado" class="modal-overlay" @click="closeValidacaoModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-check-double"></i>
            Validação de Envios - Inscrição #{{ grupoSelecionado.inscricaoId ?? grupoSelecionado.inscricao_id }}
          </h4>
          <button @click="closeValidacaoModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Status Atual -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-info-circle"></i>
              Status Atual
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Pendentes</label>
                <span class="badge badge-warning">{{ grupoSelecionado.pendentes || 0 }}</span>
              </div>
              <div class="detail-item">
                <label>Total de Envios</label>
                <span class="badge badge-info">{{ (grupoSelecionado.totalEnvios ?? grupoSelecionado.total_envios) || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Informações do Candidato -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-user"></i>
              Dados do Candidato
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Nome</label>
                <span>{{ grupoSelecionado.inscricao?.cadastro?.nome }}</span>
              </div>
              <div class="detail-item">
                <label>CPF</label>
                <span>{{ formatCPF(grupoSelecionado.inscricao?.cadastro?.cpf) }}</span>
              </div>
              <div class="detail-item">
                <label>E-mail</label>
                <span>{{ grupoSelecionado.inscricao?.cadastro?.email }}</span>
              </div>
              <div class="detail-item">
                <label>Telefone</label>
                <span>{{ grupoSelecionado.inscricao?.cadastro?.telefone_celular || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Informações do Concurso -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-clipboard-list"></i>
              Dados do Concurso
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Prefeitura</label>
                <span>{{ grupoSelecionado.inscricao?.concurso?.prefeitura }}</span>
              </div>
              <div class="detail-item">
                <label>Título</label>
                <span>{{ grupoSelecionado.inscricao?.concurso?.titulo }}</span>
              </div>
              <div class="detail-item">
                <label>Cargo</label>
                <span>{{ grupoSelecionado.inscricao?.vaga?.cargo }}</span>
              </div>
              <div class="detail-item">
                <label>Nível</label>
                <span>{{ grupoSelecionado.inscricao?.vaga?.nivel || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Envios da inscrição -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-file-upload"></i>
              Envios
            </h5>
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Prazo (Pontuação)</th>
                    <th>Anexo</th>
                    <th>Situação</th>
                    <th>Pontos</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in validacoes" :key="item.envioId">
                    <td>
                      <strong>#{{ item.envioId }}</strong>
                      <div class="text-muted">{{ item.descricao || 'Documento' }}</div>
                    </td>
                    <td>
                      <div class="text-muted">{{ item.prazoDescricao || '-' }}</div>
                      <small class="text-muted">Pontuação padrão: {{ item.pontuacaoPrazo ?? 0 }}</small>
                    </td>
                    <td>
                      <a v-if="item.caminhoAnexo" :href="item.caminhoAnexo" target="_blank" class="btn btn-sm btn-info">
                        <i class="fas fa-file-download"></i>
                        Ver
                      </a>
                      <span v-else class="text-muted">Sem anexo</span>
                    </td>
                    <td>
                      <select v-model="item.situacao" class="form-control form-control-sm" @change="onChangeSituacao(idx)">
                        <option value="P">Pendente</option>
                        <option value="A">Validar</option>
                        <option value="R">Rejeitar</option>
                      </select>
                    </td>
                    <td style="max-width: 120px">
                      <input
                        v-model.number="item.pontos"
                        type="number"
                        step="0.01"
                        class="form-control form-control-sm"
                        :disabled="item.situacao !== 'A'"
                        placeholder="0"
                      />
                    </td>
                    <td>
                      <textarea v-model="item.resposta" class="form-control form-control-sm" rows="2" placeholder="Observação (opcional)"></textarea>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div class="modal-footer">
          <button @click="closeValidacaoModal" class="btn btn-secondary">
            Fechar
          </button>
          <button 
            @click="salvarValidacaoLote" 
            class="btn btn-primary"
            :disabled="salvando || validacoes.length === 0"
          >
            <i v-if="salvando" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ salvando ? 'Salvando...' : 'Salvar Validações' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'EnviosLista',
  data() {
    return {
      loading: false,
      salvando: false,
      grupos: [],
      concursos: [],
      estatisticas: {},
      filters: {
        search: '',
        concursoId: '',
        status: '',
        dataInicio: '',
        dataFim: ''
      },
      sortOrder: 'desc',
      currentPage: 1,
      perPage: 15,
      totalItems: 0,
      totalPages: 0,
      searchTimeout: null,
      showValidacaoModal: false,
      grupoSelecionado: null,
      validacoes: []
    }
  },
  computed: {
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, this.currentPage + 2)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    this.loadConcursos()
    this.loadEnvios()
    this.loadEstatisticas()
  },
  methods: {
    async loadConcursos() {
      try {
        const response = await axios.get('/ps/concurso')
        this.concursos = response.data
      } catch (error) {
        console.error('Erro ao carregar concursos:', error)
      }
    },

    async loadEnvios() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          sort_order: this.sortOrder,
          search: this.filters.search,
          concurso_id: this.filters.concursoId,
          status: this.filters.status,
          data_inicio: this.filters.dataInicio,
          data_fim: this.filters.dataFim
        }

        const response = await axios.get('/ps/envio/admin/agrupados', { params })
        this.grupos = response.data.data || []
        this.totalItems = response.data.pagination?.total || 0
        this.totalPages = (response.data.pagination?.lastPage ?? response.data.pagination?.last_page) || 0
      } catch (error) {
        console.error('Erro ao carregar envios:', error)
        this.$sweet.showError('Erro ao carregar envios')
      } finally {
        this.loading = false
      }
    },

    async loadEstatisticas() {
      try {
        const params = { ...this.filters }
        const response = await axios.get('/ps/envio/admin/estatisticas', { params })
        this.estatisticas = response.data
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error)
      }
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.applyFilters()
      }, 500)
    },

    applyFilters() {
      this.currentPage = 1
      this.loadEnvios()
      this.loadEstatisticas()
    },

    clearFilters() {
      this.filters = {
        search: '',
        concursoId: '',
        status: '',
        dataInicio: '',
        dataFim: ''
      }
      this.currentPage = 1
      this.loadEnvios()
      this.loadEstatisticas()
    },

    // ordenação por grupos (apenas por último envio por enquanto)

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadEnvios()
      }
    },

    async viewDetails(grupo) {
      this.grupoSelecionado = grupo
      this.showValidacaoModal = true
      
      // Montar formulário de validação em lote a partir dos envios do grupo
      const envios = Array.isArray(grupo.envios) ? grupo.envios : []
      this.validacoes = envios.map(e => {
        const prazo = e?.prazoEnvio ?? e?.prazo_envio ?? null
        const pontPrazoRaw = prazo?.pontuacao ?? 0
        const pontPrazo = Number(String(pontPrazoRaw).replace(',', '.')) || 0
        const situacao = e?.situacao || 'P'
        const pontosAtual = e?.pontos !== null && e?.pontos !== undefined ? Number(e.pontos) : (situacao === 'A' ? pontPrazo : 0)

        return {
          envioId: e.id,
          descricao: e.descricao,
          caminhoAnexo: e?.caminhoAnexo ?? e?.caminho_anexo ?? '',
          prazoDescricao: prazo?.descricao ?? '',
          pontuacaoPrazo: pontPrazo,
          situacao,
          pontos: pontosAtual,
          resposta: e?.resposta || ''
        }
      })
    },

    onChangeSituacao(idx) {
      const item = this.validacoes[idx]
      if (!item) return

      // Ao validar, preencher pontos padrão se estiver vazio
      if (item.situacao === 'A' && (item.pontos === null || item.pontos === '' || Number.isNaN(Number(item.pontos)))) {
        item.pontos = item.pontuacaoPrazo || 0
      }
    },

    closeValidacaoModal() {
      this.showValidacaoModal = false
      this.grupoSelecionado = null
      this.validacoes = []
    },

    async salvarValidacaoLote() {
      if (!this.grupoSelecionado) return

      const { value } = await this.$sweet.showConfirm(
        'Confirmar Validações',
        'Deseja salvar as validações de <b>todos</b> os envios desta inscrição?'
      )
      if (!value) return

      this.salvando = true
      try {
        const inscricaoId = this.grupoSelecionado.inscricaoId ?? this.grupoSelecionado.inscricao_id
        const payload = {
          inscricao_id: inscricaoId,
          itens: this.validacoes.map(v => ({
            envio_id: v.envioId,
            situacao: v.situacao,
            resposta: v.resposta,
            pontos: v.pontos
          }))
        }

        await axios.put(`/ps/envio/admin/validar-lote`, payload)
        
        this.$sweet.showSuccess('Validações registradas com sucesso!')
        this.closeValidacaoModal()
        this.loadEnvios()
        this.loadEstatisticas()
      } catch (error) {
        console.error('Erro ao salvar validação:', error)
        this.$sweet.showError('Erro ao salvar validação')
      } finally {
        this.salvando = false
      }
    },

    async exportData() {
      try {
        this.$sweet.showInfo('Gerando arquivo Excel...')
        
        // Mapear filtros para o formato esperado pelo backend (snake_case)
        const params = {
          search: this.filters.search,
          concurso_id: this.filters.concursoId,
          status: this.filters.status,
          data_inicio: this.filters.dataInicio,
          data_fim: this.filters.dataFim
        }

        const response = await axios.get('/ps/envio/admin/exportar-excel', {
          params,
          responseType: 'blob'
        })
        
        // Criar download do arquivo
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `envios_${new Date().getTime()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$sweet.showSuccess('Arquivo exportado com sucesso!')
      } catch (error) {
        console.error('Erro ao exportar dados:', error)
        this.$sweet.showError('Erro ao exportar dados')
      }
    },

    formatCPF(cpf) {
      if (!cpf) return '-'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },

    formatDate(date) {
      if (!date) return '-';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = date.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof date === 'string') {
          let dateString = date.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return '-';
      }
    },

    getStatusClass(status) {
      return {
        'badge badge-warning': status === 'P',
        'badge badge-success': status === 'A',
        'badge badge-danger': status === 'R'
      }
    },

    getStatusText(status) {
      const statuses = {
        'P': 'Pendente',
        'A': 'Validado',
        'R': 'Rejeitado'
      }
      return statuses[status] || status
    }
  }
}
</script>

<style lang="scss" scoped>
.envios-lista {
  .page-header {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .page-title {
      margin: 0 0 8px 0;
      font-size: 24px;
      font-weight: 600;
      color: #2c3e50;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
      color: #667eea;
      }
    }

    .page-subtitle {
      margin: 0;
      font-size: 14px;
      color: #6c757d;
    }
  }

  .filters-card {
    background: white;
    border-radius: 15px;
    padding: 20px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .filters-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
      align-items: end;

      .filter-group {
        label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          color: #495057;
          font-size: 14px;
        }

        .form-control {
          width: 100%;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 14px;
          transition: border-color 0.3s ease;

          &:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
        }
      }

      .filter-actions {
        display: flex;
        gap: 10px;
        grid-column: 1 / -1;
        justify-content: center;
        margin-top: 10px;

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          cursor: pointer;

          &.btn-secondary {
            background: #6c757d;
            color: white;

            &:hover {
              background: #5a6268;
            }
          }

          &.btn-success {
            background: #28a745;
            color: white;

            &:hover {
              background: #218838;
            }
          }
        }
      }
    }
  }

  .stats-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
      margin-bottom: 20px;

    .stat-card {
      background: white;
      border-radius: 15px;
      padding: 25px;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      border: 1px solid rgba(0, 0, 0, 0.05);
      display: flex;
      align-items: center;
      gap: 20px;

      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 24px;
      }

      &.pending .stat-icon {
        background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
      }

      &.success .stat-icon {
        background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
      }

      &.danger .stat-icon {
        background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
      }

      .stat-content {
        .stat-value {
          font-size: 28px;
          font-weight: 700;
      color: #2c3e50;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 14px;
          color: #6c757d;
          font-weight: 500;
        }
      }
    }
  }

  .table-card {
    background: white;
    border-radius: 15px;
    padding: 0;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;

    .loading-state, .empty-state {
      padding: 60px 30px;
      text-align: center;

      i {
        font-size: 48px;
        color: #667eea;
        margin-bottom: 15px;
        opacity: 0.7;
      }

      p, h3 {
      margin: 0;
      color: #6c757d;
      }

      h3 {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 8px;
        color: #495057;
      }
    }

    .table-responsive {
      overflow-x: auto;
    }

    .table {
      margin: 0;
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;

      thead {
        background: #f8f9fa;

        th {
          padding: 15px 12px;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #e9ecef;
          font-size: 14px;
          white-space: nowrap;

          &.sortable {
            cursor: pointer;
            user-select: none;
            transition: background-color 0.3s ease;

            &:hover {
              background: #e9ecef;
            }

            i {
              margin-left: 5px;
              opacity: 0.5;
            }
          }
        }
      }

      tbody {
        tr {
          transition: background-color 0.3s ease;

          &:hover {
            background: #f8f9fa;
          }

          td {
            padding: 12px;
            border-bottom: 1px solid #e9ecef;
            vertical-align: middle;
            font-size: 14px;

            .candidate-info, .concurso-info {
              strong {
                color: #2c3e50;
                display: block;
                margin-bottom: 2px;
                line-height: 1.3;
              }

              small {
                color: #6c757d;
                font-size: 11px;
                line-height: 1.2;
                max-height: 32px;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
              }
            }

            .badge {
              font-size: 12px;
              padding: 4px 8px;
              border-radius: 12px;
              font-weight: 500;
              display: inline-flex;
              align-items: center;
              gap: 4px;

              &.badge-warning {
                background: #ffc107;
                color: #212529;
              }

              &.badge-success {
                background: #28a745;
                color: white;
              }

              &.badge-danger {
                background: #dc3545;
                color: white;
              }

              &.badge-info {
                background: #17a2b8;
                color: white;
              }

              &.badge-primary {
                background: #667eea;
                color: white;
              }

              i {
                font-size: 10px;
              }
            }

            &.actions {
              text-align: center;
              white-space: nowrap;

              .btn {
                margin: 0 2px;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 12px;
                border: none;
                transition: all 0.3s ease;
                cursor: pointer;

                &.btn-info {
                  background: #17a2b8;
                  color: white;

                  &:hover {
                    background: #138496;
                  }
                }
              }
            }
          }
        }
      }
    }

    .pagination-wrapper {
      padding: 20px 30px;
      border-top: 1px solid #e9ecef;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8f9fa;

      .pagination {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        gap: 5px;

        .page-item {
          .page-link {
            padding: 8px 12px;
            border: 1px solid #e9ecef;
            color: #667eea;
            background: white;
            border-radius: 6px;
            transition: all 0.3s ease;
            cursor: pointer;

            &:hover {
              background: #667eea;
              color: white;
              border-color: #667eea;
            }
          }

          &.active .page-link {
            background: #667eea;
            color: white;
            border-color: #667eea;
          }

          &.disabled .page-link {
            color: #6c757d;
            background: #f8f9fa;
            cursor: not-allowed;

            &:hover {
              background: #f8f9fa;
              color: #6c757d;
              border-color: #e9ecef;
            }
          }
        }
      }

      .pagination-info {
        font-size: 14px;
        color: #6c757d;
      }
    }
  }

  // Modal Styles
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
  }

  .modal-content {
    background: white;
    border-radius: 15px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

    &.large {
      max-width: 900px;
    }

    .modal-header {
      padding: 20px 25px;
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f8f9fa;

      .modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          color: #667eea;
        }
      }

      .modal-close {
        background: none;
        border: none;
        font-size: 20px;
        color: #6c757d;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.3s ease;

        &:hover {
          background: #e9ecef;
          color: #495057;
        }
      }
    }

    .modal-body {
      padding: 25px;

      .details-section {
        margin-bottom: 25px;
        padding-bottom: 25px;
        border-bottom: 1px solid #e9ecef;

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .section-title {
          margin: 0 0 15px 0;
          font-size: 16px;
          font-weight: 600;
          color: #495057;
          display: flex;
          align-items: center;
          gap: 8px;

          i {
            color: #667eea;
            font-size: 14px;
          }
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;

          .detail-item {
            &.full-width {
              grid-column: 1 / -1;
            }

            label {
              display: block;
              font-weight: 600;
      color: #6c757d;
              font-size: 13px;
              margin-bottom: 5px;
            }

            span {
              display: block;
              color: #2c3e50;
              font-size: 14px;
            }

            .link-file {
              color: #667eea;
              text-decoration: none;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
              padding: 10px 15px;
              background: #f0f3ff;
              border-radius: 8px;
              transition: all 0.3s ease;

              &.large {
                font-size: 15px;
                padding: 12px 20px;
              }

              &:hover {
                background: #667eea;
                color: white;
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
              }

              i {
                font-size: 14px;
              }
            }
          }
        }

        .validacao-form {
          .form-group {
            margin-bottom: 15px;

            label {
              display: block;
              font-weight: 600;
              color: #495057;
              margin-bottom: 8px;
              font-size: 14px;
            }

            .form-control {
              width: 100%;
              padding: 10px 12px;
              border: 2px solid #e9ecef;
              border-radius: 8px;
              font-size: 14px;
              transition: border-color 0.3s ease;

              &:focus {
                outline: none;
                border-color: #667eea;
                box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
              }
            }

            textarea.form-control {
              resize: vertical;
              min-height: 100px;
            }
          }
        }
      }
    }

    .modal-footer {
      padding: 15px 25px;
      border-top: 1px solid #e9ecef;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      background: #f8f9fa;

      .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;

        &.btn-secondary {
          background: #6c757d;
          color: white;

          &:hover {
            background: #5a6268;
          }
        }

        &.btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;

          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
          }

          &:disabled {
            background: #6c757d;
            cursor: not-allowed;
            transform: none;
          }
        }
      }
    }
  }
}

// Responsividade
@media (max-width: 768px) {
  .envios-lista {
    .filters-card .filters-row {
      grid-template-columns: 1fr;
    }

    .stats-cards {
      grid-template-columns: 1fr;
    }

    .table-card .pagination-wrapper {
      flex-direction: column;
      gap: 15px;
      text-align: center;
    }
  }
}
</style>
